import { ConfirmationDialog } from "@components/ConfirmationDialog";
import { FormInput } from "@components/FormInput";
import { FormMultiSelect } from "@components/FormMultiSelect";
import { FormSelect } from "@components/FormSelect";
import { Container } from "@components/crud/Container";
import { Footer } from "@components/crud/Footer";
import { Form } from "@components/crud/Form";
import { Loader } from "@components/crud/Loader";
import { Toolbar } from "@components/crud/Toolbar";
import { useApiSelectOptions } from "@hooks/useApiSelectOptions";
import Grid from "@mui/material/Unstable_Grid2";
import {
  ModelMedia,
  useAdminGameConceptGet,
  useAdminGameSystemSystemIdGet,
  useAdminGameSystemSystemIdPut,
  useAdminPositionGet,
  useAdminSkillGet,
  useAdminSportGet
} from "@sportsgravyengineering/sg-api-react-sdk";
import { useSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import { Typography, FormLabel } from "@mui/material";
import { styled } from "@mui/material/styles";
import GamePickerImage from "@assets/images/gameSystemPicker.png";
import { MediaSelector } from "@components/MediaSelector";
import { uploadMediaUsingPresignedUrl } from "@services/customNetworkCalls";
import { useRecoilState } from "recoil";
import { organizationAtom } from "@recoil/auth";
import { capitalizeEveryWord } from "@utils/capitalize";

const StyledFormLabel = styled(FormLabel)(({ theme }) => ({
  marginBottom: "0.25rem",

  "& .MuiFormLabel-asterisk": {
    color: theme.palette.error.main
  }
}));

export const GameSystemEdit = () => {
  const [filestoUpload, setFilesToUpload] = useState<(File | ModelMedia)[]>([]);
  const navigate = useNavigate();
  const { systemId } = useParams();
  const [organizationId] = useRecoilState(organizationAtom);
  const { enqueueSnackbar } = useSnackbar();
  const [openCancelDialog, setOpenCancelDialog] = useState(false);
  const [positions, setPositions] = useState([]);
  const [skills, setSkills] = useState([]);
  const [gameConcepts, setGameConcepts] = useState([]);
  const [files, setFiles] = useState<ModelMedia[]>([]);
  const [mediaUploadLoading, setMediaUploadLoading] = useState(false);
  const {
    data: gameSystem,
    isFetching: isGameSystemFetching,
    error: error
  } = useAdminGameSystemSystemIdGet(systemId as string);
  useEffect(() => {
    if (error?.code == "ERR_BAD_REQUEST") navigate("/not-found");
  }, [error]);
  const { data: sports, isLoading: isSportLoading } = useAdminSportGet({
    organizationId: organizationId!
  });

  const sportOptions = useMemo(
    () =>
      sports?.data?.map((sport) => ({
        label: sport.name!,
        value: sport.sportId!
      })) || [],
    [sports]
  );
  const { options: positionOptions, isLoading: positionOptionsLoading } =
    useApiSelectOptions({
      api: useAdminPositionGet,
      dataField: "positions",
      labelField: "name",
      valueField: "positionId",
      params: {
        sportId: positions || gameSystem?.data?.sportId
      },
      options: {
        query: {
          enabled: !!positions || !!gameSystem?.data?.sportId
        }
      }
    });

  const { options: skillOptions, isLoading: skillOptionsLoading } =
    useApiSelectOptions({
      api: useAdminSkillGet,
      dataField: "skills",
      labelField: "name",
      valueField: "skillId",
      params: {
        sportId: skills || gameSystem?.data?.sportId
      },
      options: {
        query: {
          enabled: !!skills || !!gameSystem?.data?.sportId
        }
      }
    });

  const { options: gameConceptOptions, isLoading: gameConceptOptionsLoading } =
    useApiSelectOptions({
      api: useAdminGameConceptGet,
      dataField: "gameConcepts",
      labelField: "name",
      valueField: "conceptId",
      params: {
        sportId: gameConcepts || gameSystem?.data?.sportId
      },
      options: {
        query: {
          enabled: !!gameConcepts || !!gameSystem?.data?.sportId
        }
      }
    });

  const {
    handleSubmit,
    control,
    formState: { isValid, isDirty },
    setValue,
    reset
  } = useForm({
    mode: "onBlur"
  });
  const checkSaveDisabled = () => {
    if (filestoUpload && gameSystem?.data.media) {
      return filestoUpload
        ? !isDirty &&
          gameSystem?.data?.media?.length > filestoUpload.length &&
          filestoUpload.filter((file) => file instanceof File).length != 0 //file removed, no new file added
          ? false
          : !isDirty &&
            filestoUpload.filter((file) => file instanceof File).length != 0 //new file added
          ? false
          : isDirty &&
            filestoUpload.filter((file) => file instanceof File).length == 0 //done other changes
          ? false
          : isDirty &&
            filestoUpload.filter((file) => file instanceof File).length != 0 //done other changes with new file added
          ? false
          : !isDirty &&
            filestoUpload &&
            filestoUpload.filter((file) => file instanceof File).length == 0 &&
            gameSystem?.data?.media?.length == filestoUpload.length
          ? true //no changes
          : false
        : false;
    }
  };
  useEffect(() => {
    if (!isGameSystemFetching) {
      reset(
        {
          name: gameSystem?.data?.name,
          description: gameSystem?.data?.description || "",
          sportId: gameSystem?.data?.sportId,
          positions: gameSystem?.data?.positions?.map(
            (position) => position.positionId
          ),
          skills: gameSystem?.data?.skills?.map((skill) => skill.skillId),
          gameConcepts: gameSystem?.data?.gameConcepts?.map(
            (gameConcept) => gameConcept.conceptId
          )
        },
        {
          keepDirtyValues: true
        }
      );
      setFiles(gameSystem?.data?.media || []);
    }
  }, [gameSystem]);

  const { mutate: save, isLoading: isSaving } = useAdminGameSystemSystemIdPut();
  const saveHandler = async (formValues) => {
    setMediaUploadLoading(true);
    const filesPromises = await Promise.all(
      filestoUpload.map((file) => {
        if (file instanceof File) {
          const promise = uploadMediaUsingPresignedUrl(file);
          console.log("PROMISE", promise);
          return promise;
        } else {
          return file.mediaId;
        }
      })
    );
    const values = {
      ...formValues
    };
    if (values.positions) {
      values.positions = values.positions.map((position) => ({
        positionId: position
      }));
    }
    if (values.skills) {
      values.skills = values.skills.map((skill) => ({
        skillId: skill
      }));
    }
    if (values.gameConcepts) {
      values.gameConcepts = values.gameConcepts.map((gameConcept) => ({
        gameConceptId: gameConcept
      }));
    }
    values["mediaIds"] = filesPromises;
    setMediaUploadLoading(false);
    save(
      {
        systemId: systemId as string,
        data: values
      },
      {
        onSuccess: () => {
          enqueueSnackbar("Game system saved successfully!", {
            variant: "success"
          });
          navigate(`/game-systems/${systemId}`);
        },
        onError: () => {
          enqueueSnackbar("Failed to save Game System!", { variant: "error" });
        }
      }
    );
  };
  return (
    <Container>
      <Toolbar title="Edit Game System" />
      <Form>
        <Loader
          isLoading={
            isGameSystemFetching ||
            positionOptionsLoading ||
            skillOptionsLoading ||
            gameConceptOptionsLoading
          }
        >
          <Grid data-testid="gameSystem-edit-form" container spacing={3}>
            <Grid data-testid="gameSystem-name" xs={12} md={6}>
              <FormInput
                control={control}
                name="name"
                type="text"
                label="Name"
                required={true}
                onChange={(e) => {
                  setValue("name", capitalizeEveryWord(e.target.value));
                }}
                rules={{
                  required: "Name is required"
                }}
              />
            </Grid>
            <Grid data-testid="gameSystem-sport" xs={12} md={6}>
              <FormSelect
                control={control}
                name="sportId"
                label="Sport"
                isLoading={isSportLoading}
                disabled={sportOptions.length === 1}
                required={true}
                options={sportOptions}
                onChange={(e) => {
                  setValue("positions", []);
                  setPositions(e.target.value);
                  setValue("skills", []);
                  setSkills(e.target.value);
                  setValue("gameConcepts", []);
                  setGameConcepts(e.target.value);
                }}
                rules={{
                  required: "Sport is required"
                }}
              />
            </Grid>
            <Grid data-testid="gameSystem-description" xs={12} md={6}>
              <FormInput
                control={control}
                name="description"
                type="text"
                label="Description"
                multiline={true}
              />
            </Grid>
            <Grid data-testid="gameSystem-position" xs={12} md={6}>
              <FormMultiSelect
                control={control}
                name="positions"
                label="Positions"
                value="positions"
                required={false}
                options={positionOptions}
              />
            </Grid>
            <Grid data-testid="gameSystem-skill" xs={12} md={6}>
              <FormMultiSelect
                control={control}
                name="skills"
                label="Skills"
                value="skills"
                required={false}
                options={skillOptions}
              />
            </Grid>
            <Grid data-testid="gameSystem-gameConcept" xs={12} md={6}>
              <FormMultiSelect
                control={control}
                name="gameConcepts"
                label="Game Concepts"
                value="gameConcepts"
                required={false}
                options={gameConceptOptions}
              />
            </Grid>
            <Grid xs={12} md={12}>
              <StyledFormLabel>
                <Typography display="inline" variant="formLabel">
                  {"Photo/Video Content"}
                </Typography>
              </StyledFormLabel>
            </Grid>
            <MediaSelector
              setFilesToUpload={(file) => {
                setFilesToUpload(file);
              }}
              uploadedFiles={files}
              imagePlaceHolder={GamePickerImage}
            />
          </Grid>
        </Loader>
      </Form>
      <Footer
        cancelBtnClick={() => setOpenCancelDialog(true)}
        saveBtnClick={handleSubmit(saveHandler)}
        isDisabled={
          !isValid || isSaving || mediaUploadLoading || checkSaveDisabled()
        }
        isLoading={isSaving || mediaUploadLoading}
      />
      <ConfirmationDialog
        title="Are you sure you want to cancel?"
        body="All of your current changes will be lost."
        open={openCancelDialog}
        close={() => setOpenCancelDialog(false)}
        onCancel={() => setOpenCancelDialog(false)}
        onConfirm={() => navigate("/game-systems")}
        cancelBtnText="Cancel"
        confirmBtnText="Confirm"
      />
    </Container>
  );
};
